import type {
  AuthStatus,
  CopierStatus,
  Environment,
  GenerateJob,
  GenerateRequest,
  GenerateResult,
  GenerateStatus,
  TemplateInfo,
  TemplateQuestions,
} from '@new_project/shared';

/**
 * Typed client for the generator backend. In OAuth mode the session cookie
 * (credentials: 'include') carries authentication; in the PAT fallback mode the
 * GitLab token is passed via the X-Gitlab-Token header (kept in React state
 * only, never persisted). API base is configurable via VITE_API_URL.
 */
export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  public readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, token?: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { 'x-gitlab-token': token } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(res.status, body.message ?? res.statusText);
  }
  return (await res.json()) as T;
}

export const api = {
  // OAuth session (cookie-based; token param stays for the manual fallback).
  getAuthStatus: (): Promise<AuthStatus> => request<AuthStatus>('/auth/me'),

  logout: (): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>('/auth/logout', undefined, { method: 'POST' }),

  // Environment preflight (local tooling - no GitLab token needed).
  getEnvironment: (): Promise<Environment> => request<Environment>('/environment'),

  installCopier: (): Promise<CopierStatus> =>
    request<CopierStatus>('/environment/install-copier', undefined, { method: 'POST' }),

  listTemplates: (token: string): Promise<TemplateInfo[]> =>
    request<TemplateInfo[]>('/templates', token),

  getQuestions: (token: string, templatePath: string): Promise<TemplateQuestions> =>
    request<TemplateQuestions>(`/templates/${encodeURIComponent(templatePath)}/questions`, token),

  generate: (
    token: string,
    body: GenerateRequest,
    onStep: (label: string) => void,
  ): Promise<GenerateResult> => runGenerateJob(token, body, onStep),
};

const POLL_INTERVAL_MS = 600;
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Start a generation job, then poll its status until it finishes. Calls `onStep`
 * for each newly-reported step label, resolves with the result on success, and
 * throws ApiError on failure (or on a pre-start validation/auth error).
 *
 * Polling (not streaming) makes progress robust: it never depends on the server
 * flushing response chunks across the network or the browser surfacing them.
 */
async function runGenerateJob(
  token: string,
  body: GenerateRequest,
  onStep: (label: string) => void,
): Promise<GenerateResult> {
  const { jobId } = await request<GenerateJob>('/generate', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  let delivered = 0;
  for (;;) {
    const status = await request<GenerateStatus>(`/generate/${encodeURIComponent(jobId)}`, token);
    for (const label of status.steps.slice(delivered)) onStep(label);
    delivered = status.steps.length;

    if (status.status === 'done') {
      if (!status.result) throw new ApiError(500, 'Job finished without a result.');
      return status.result;
    }
    if (status.status === 'error') {
      throw new ApiError(500, status.error ?? 'Generation failed.');
    }
    await sleep(POLL_INTERVAL_MS);
  }
}
